import React from "react";
import { RecIcon } from "../materials/MaterialRecordParts";
import { ProjectLocationsSection } from "./ProjectLocationsSection";
import { ProjectOfflineBadge } from "./ProjectOfflineBadge";
import { ProjectHealthStrip } from "./ProjectHealthStrip";
import { photoCountLabel } from "../../helpers/projectPictures";
import "../materials/materialRecord.css";
import "./projects.css";

function SectionLabel({ children }) {
  return <div className="pj-label">{children}</div>;
}

/** A drill-down money tile (Labour / Materials / Other Costs). */
function SummaryTile({ icon, label, value, onClick, wide = false }) {
  return (
    <button
      type="button"
      className={`pj-tile pj-tile--stack${wide ? " pj-tile--wide" : ""}`}
      onClick={onClick}
    >
      <span className="pj-tile__head">
        <span className="pj-tile__icon">
          <RecIcon name={icon} />
        </span>
        <span className="pj-tile__label">{label}</span>
        <RecIcon name="chevron" className="pj-tile__chev" />
      </span>
      <span className="pj-tile__value">{value}</span>
    </button>
  );
}

function NavRow({ icon, title, subtitle, onClick }) {
  return (
    <button type="button" className="pj-navrow" onClick={onClick}>
      <span className="pj-navrow__icon">
        <RecIcon name={icon} />
      </span>
      <span className="pj-navrow__text">
        <span className="pj-navrow__title">{title}</span>
        {subtitle ? <span className="pj-navrow__sub">{subtitle}</span> : null}
      </span>
      <RecIcon name="chevron" className="pj-navrow__chev" />
    </button>
  );
}

/**
 * Project landing: ONE scrolling page. Identity → summary (hours + the
 * cost drill-downs) → location → sections. Presentation only: every number
 * and handler comes from ProjectDetailScreen.
 */
export function ProjectWorkspaceHome({
  title = "",
  clientName,
  projectNumber,
  statusLabel,
  offlineState = "",
  onRetryOffline,
  showEdit,
  onEdit,
  totalHoursLabel,
  totalHoursEmphasis = false,
  showFinancial = false,
  showOperationalMaterials = false,
  labourLabel = "$0.00",
  materialsLabel = "$0.00",
  otherCostsLabel = "$0.00",
  totalLabel = "$0.00",
  totalEmphasis = false,
  address = "",
  description = "",
  projectId,
  editable = false,
  onOpenLocation,
  locations,
  sessionCount = 0,
  pictureCount = 0,
  showTasks = false,
  tasksLabel = "",
  onOpenTasks,
  showConversations = false,
  onOpenConversations,
  showClientView = false,
  clientViewLabel = "",
  onOpenClientView,
  showInvoicing = false,
  onOpenPictures,
  onOpenActivity,
  onOpenLabour,
  onOpenMaterials,
  onOpenOtherCosts,
  onOpenInvoicing,
  health = null,
  targetLabel = "",
}) {
  const sessionsLabel = `${sessionCount} session${sessionCount === 1 ? "" : "s"}`;
  const showOffline = offlineState && offlineState !== "attention";

  // The `pj-ws__*` wrappers are layout hooks only: they are `display: contents`
  // below 1024px (mobile DOM flow unchanged) and become grid areas on desktop.
  return (
    <div className="pj-ws">
      <section className="pj-identity pj-ws__identity">
        {title ? <h1 className="pj-identity__name">{title}</h1> : null}
        {statusLabel || showOffline ? (
          <div className="pj-identity__chips">
            {statusLabel ? <span className="pj-status">{statusLabel}</span> : null}
            {showOffline ? <ProjectOfflineBadge state={offlineState} /> : null}
            {projectNumber ? <span className="pj-identity__num">{projectNumber}</span> : null}
          </div>
        ) : null}
        {offlineState === "attention" ? (
          <ProjectOfflineBadge state={offlineState} block onRetry={onRetryOffline} />
        ) : null}
        <div className="pj-client">
          <span className="pj-client__icon">
            <RecIcon name="building" />
          </span>
          <span className="pj-client__text">
            <span className="pj-client__label">Client</span>
            <span className="pj-client__value">{clientName || "—"}</span>
            {showEdit ? (
              <button type="button" className="pj-link" onClick={onEdit}>
                Edit details
              </button>
            ) : null}
          </span>
        </div>
      </section>

      {health ? (
        <div className="pj-ws__health">
          <ProjectHealthStrip health={health} targetLabel={targetLabel} onOpenTasks={showTasks ? onOpenTasks : undefined} />
        </div>
      ) : null}

      <div className="pj-ws__summary">
      <SectionLabel>Project Summary</SectionLabel>
      <div className="pj-summary">
        <div className={`pj-tile pj-tile--hero${totalHoursEmphasis ? " is-emph" : ""}`}>
          <span className="pj-tile__icon">
            <RecIcon name="clock" />
          </span>
          <span className="pj-tile__body pj-tile__body--row">
            <span className="pj-tile__label">Total Hours</span>
            <span className="pj-tile__value pj-tile__value--lg">{totalHoursLabel}</span>
          </span>
        </div>
        {showFinancial ? (
          <>
            <div className="pj-summary__pair">
              <SummaryTile icon="user" label="Labour" value={labourLabel} onClick={onOpenLabour} />
              <SummaryTile
                icon="cart"
                label="Materials"
                value={materialsLabel}
                onClick={onOpenMaterials}
              />
            </div>
            <SummaryTile
              icon="invoice"
              label="Other Costs"
              value={otherCostsLabel}
              onClick={onOpenOtherCosts}
              wide
            />
            <div className={`pj-tile pj-tile--total${totalEmphasis ? " is-emph" : ""}`}>
              <span className="pj-tile__icon">
                <RecIcon name="dollar" />
              </span>
              <span className="pj-tile__body pj-tile__body--row">
                <span className="pj-tile__label">Total Project Cost</span>
                <span className="pj-tile__value pj-tile__value--lg">{totalLabel}</span>
              </span>
            </div>
          </>
        ) : null}
      </div>
      </div>

      {address ? (
        <div className="pj-ws__address">
          <SectionLabel>Site Address</SectionLabel>
          <div className="pj-address">
            <span className="pj-address__icon">
              <RecIcon name="pin" />
            </span>
            <span className="pj-address__text">{address}</span>
          </div>
        </div>
      ) : null}

      {projectId && (
        <div className="pj-ws__locations">
          <ProjectLocationsSection
            projectId={projectId}
            editable={editable}
            onOpenLocation={onOpenLocation}
            locations={locations}
          />
        </div>
      )}

      {description ? (
        <div className="pj-ws__desc">
          <SectionLabel>Description</SectionLabel>
          <div className="pj-desc">{description}</div>
        </div>
      ) : null}

      <div className="pj-ws__sections">
      <SectionLabel>Project Sections</SectionLabel>
      <div className="pj-list">
        {showTasks && (
          <NavRow icon="task" title="Tasks" subtitle={tasksLabel} onClick={onOpenTasks} />
        )}
        {showConversations && (
          <NavRow
            icon="chat"
            title="Conversations"
            subtitle="Record talk and approve the tasks it produces"
            onClick={onOpenConversations}
          />
        )}
        <NavRow
          icon="image"
          title="Pictures"
          subtitle={photoCountLabel(pictureCount)}
          onClick={onOpenPictures}
        />
        <NavRow icon="chart" title="Activity" subtitle={sessionsLabel} onClick={onOpenActivity} />
        {showOperationalMaterials ? (
          <NavRow
            icon="cart"
            title="Materials"
            subtitle="Record quantities used"
            onClick={onOpenMaterials}
          />
        ) : null}
        {showInvoicing && (
          <NavRow
            icon="invoice"
            title="Invoicing"
            subtitle="Create and view invoices"
            onClick={onOpenInvoicing}
          />
        )}
        {showClientView && (
          <NavRow
            icon="share"
            title="Client View"
            subtitle={clientViewLabel}
            onClick={onOpenClientView}
          />
        )}
      </div>
      </div>
    </div>
  );
}
